import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { calculateImageDimension } from '@/components/common/media-content/calculate-image-dimension';
import { useSafeMediaUrl } from '@/hooks/use-safe-media-url';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { canParseAsUrl } from '@/utils/parse-url';

export type PreloadImageType = 'url' | 'blob' | 'base64url';

interface Options {
  maxWidth?: number;
  as?: PreloadImageType;
  size?: [number, number];
  onError?: () => void;
}

interface ImageMetadata {
  width: number;
  height: number;
  url: string;
}

function retrieveImageMetadata(src: string, options: Pick<Options, 'as' | 'size'>): Promise<ImageMetadata> {
  if (!canParseAsUrl(src)) return Promise.reject('BAD_IMAGE_URL');

  const { searchParams } = new URL(src);

  if (searchParams.has('h') && options.as === 'url') {
    const width = Number(searchParams.get('w') || 0);
    const height = Number(searchParams.get('h') || 0);
    return Promise.resolve({ width, height, aspectRatio: width / height, url: src });
  }

  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
        url: options.as === 'url' ? src : transformImageIntoString(image, options),
      });
    };
    image.onerror = () => {
      reject('IMAGE_LOAD_ERROR');
    };
    image.crossOrigin = 'anonymous';
    image.src = src;
  });
}

function putImageOntoCanvas(image: HTMLImageElement, options: Pick<Options, 'as' | 'size'>): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const canvasWidth = (canvas.width = options.size?.[0] ?? image.naturalWidth);
  const canvasHeight = (canvas.height = options.size?.[1] ?? image.naturalHeight);
  const ctx = canvas.getContext('2d');

  if (ctx) {
    const imageAspectRatio = image.naturalWidth / image.naturalHeight;
    const canvasAspectRatio = canvasWidth / canvasHeight;
    let drawWidth = canvasWidth;
    let drawHeight = canvasHeight;
    let offsetX = 0;
    let offsetY = 0;

    if (imageAspectRatio > canvasAspectRatio) {
      drawWidth = canvasHeight * imageAspectRatio;
      offsetX = -(drawWidth - canvasWidth) / 2;
    } else {
      drawHeight = canvasWidth / imageAspectRatio;
      offsetY = -(drawHeight - canvasHeight) / 2;
    }

    ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
  }

  return canvas;
}

function transformImageIntoString(image: HTMLImageElement, options: Pick<Options, 'as' | 'size'>): string {
  if (options.as === 'url') return image.src;

  const canvas = putImageOntoCanvas(image, options);

  switch (options.as) {
    case 'blob': {
      let blobUrl = '';
      canvas.toBlob((blob) => {
        if (blob) {
          blobUrl = URL.createObjectURL(blob);
        }
      });
      return blobUrl;
    }
    case 'base64url':
      return canvas.toDataURL();
    default:
      return image.src;
  }
}

const generatePreloadImageQueryOptions = (src: string, options: Pick<Options, 'as' | 'size'>) =>
  queryOptions({
    enabled: options.size === undefined || options.as !== 'url',
    queryKey: ['/PreloadImage', src.toString(), options.as, options.size?.join('x')],
    queryFn: () => retrieveImageMetadata(src, options),
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    throwOnError: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

export default function usePreloadImage(src: Nullable<string>, options: Options = {}) {
  const { size, onError, maxWidth } = options;
  const { src: safeSrc, fileEndpoint } = useSafeMediaUrl(src);
  const isDesktop = useIsDesktop();
  const { data, error } = useQuery(generatePreloadImageQueryOptions(safeSrc, options));

  const [respectiveWidth, respectiveHeight, aspectRatio] = useMemo<[number, number, number]>(() => {
    if (size) return [...size, size[0] / size[1]];

    const { width, height } = data ?? { width: 0, height: 0, url: '' };

    return calculateImageDimension(width, height, {
      maxWidth: maxWidth,
      maxHeight: isDesktop ? 0.4 : 0.25,
    });
  }, [isDesktop, data, maxWidth, size]);

  useEffect(() => {
    if (error) {
      onError?.();
    }
  }, [error, onError]);

  return {
    src: data?.url ?? safeSrc,
    fileEndpoint,
    aspectRatio,
    dimension: [respectiveWidth, respectiveHeight] as const,
  };
}
